﻿import {StringNamedValue, translation} from "@2d-front-monorepo/core";
import {
	useInitializeMvvmContext,
	Message,
	MvvmDataProvider,
	MvvmDataProviderRef,
	MvvmSelect, Icon
} from "@2d-front-monorepo/design-library";
import {PermitTranslations} from "@2d-front-monorepo/shared-translations";
import {JSX, RefObject, useCallback, useMemo} from "react";
import ResidencyPropertyFlags from "../enums/ResidencyPropertyFlags";
import useCreateSelectItems from "../helpers/useCreateSelectItems";
import isZoneChange from "../helpers/isZoneChange";
import {residencyToCompactString} from "../helpers/residencyToCompactString";
import {useScrollToFirstError} from "../helpers/scrollToFirstError";
import selectOrCreate from "../helpers/selectOrCreate";
import useEntityAttachments from "../helpers/useEntityAttachments";
import {ResidencyId} from "../nominalTypes/ResidencyId";
import {ZoneId} from "../nominalTypes/ZoneId";
import {Address} from "../types/Address";
import {EntityProfile, InstallationByHostData} from "../types/InstallationByHostData";
import {IntegraZone} from "../types/IntegraZone";
import {ResidencyResult} from "../types/ResidencyResult";
import {ComponentParts, ComponentWithChildrenParts} from "./ComponentParts";
import CreateResidencyComponent, {CreateResidencyModel} from "./CreateResidencyComponent";
import FullCreateAttachmentsComponent from "./FullCreateAttachmentsComponent";
import PermitsMvvmErrorCollector from "./PermitsMvvmErrorCollector";
import ResidencyComponent from "./ResidencyComponent";

export interface CreateOrSelectResidencyComponentProps extends ComponentParts<CreateOrSelectResidencyModel> {
	autoFocus?: boolean;
	configuration: EntityProfile<ResidencyPropertyFlags>;
	contextRef?: RefObject<MvvmDataProviderRef<CreateOrSelectResidencyModel>>;
	createEnabled?: boolean;
	hideBody?: boolean;
	createParts?: ComponentWithChildrenParts<CreateResidencyModel>;
	filesPrefix: string;
	initialState: CreateOrSelectResidencyModel;
	installation: InstallationByHostData;
	onSubmit: (formData: CreateOrSelectResidencyModel) => Promise<void>;
	residencies?: ResidencyResult[];
	selectParts?: ComponentWithChildrenParts<CreateOrSelectResidencyModel>;
	zoneIdWarning?: ZoneId;
	zones: IntegraZone[];
	provinces: StringNamedValue[];
	cities: StringNamedValue[];
	defaultProvince?: string;
	defaultCity?: string;
	onAddressBlur?: (address: Address) => void;
}

export interface CreateOrSelectResidencyModel extends CreateResidencyModel {
	residencyId: ResidencyId;
}

export default function CreateOrSelectResidencyComponent(props: CreateOrSelectResidencyComponentProps): JSX.Element {
	const {
		initialState,
		onSubmit,
		zones,
		provinces,
		cities,
		zoneIdWarning,
		installation,
		filesPrefix,
		residencies,
		contextRef,
		createEnabled,
		hideBody,
		header,
		footer,
		configuration,
		autoFocus,
		createParts,
		selectParts,
		onAddressBlur
	} = props;

	const choose = useCreateSelectItems(residencies, createEnabled, residencyToCompactString);
	const multiple = choose.length > 1;

	const {context} = useInitializeMvvmContext({
		initialState: () => {
			initialState.residencyId = selectOrCreate(choose, initialState.residencyId);
			const province = (initialState.arguments.address?.state ?? props.defaultProvince)?.toUpperCase();
			const city = (initialState.arguments.address?.city ?? props.defaultCity)?.toUpperCase();
			initialState.arguments.address = initialState.arguments.address ?? {};
			initialState.arguments.address.state = province;
			initialState.arguments.address.city = city;
			return initialState;
		},
		contextRef: contextRef
	});

	const state = context.state;
	const selectedResidency = useMemo(() => residencies?.find(r => r.id === state.residencyId), [residencies, state.residencyId]);
	const attachments = useEntityAttachments(selectedResidency, configuration.attachments);

	const createVisible = createEnabled && state.residencyId === "create";
	const warningVisible = isZoneChange(state, residencies, zoneIdWarning);
	const formRef = useScrollToFirstError(context);

	const editResidency = useCallback(function () {
		if (selectedResidency == null) {
			return;
		}

		context.update(s => {
			s.residencyId = "create";
			s.arguments.address = {...selectedResidency.address};
			s.arguments.zoneId = selectedResidency.zoneId;
			s.arguments.suffix = selectedResidency.suffix;
		});

	}, [selectedResidency]);

	return <MvvmDataProvider<CreateOrSelectResidencyModel> onSubmit={onSubmit} context={context} ref={formRef}>
		<PermitsMvvmErrorCollector/>

		{warningVisible && <Message
            type={"warning"}
            title={{text: translation(PermitTranslations.new_residency__zone_warning__title)}}
            detail={{text: translation(PermitTranslations.new_residency__zone_warning__detail)}}/>}

		{header?.(context.state)}

		<div className="layout vertical gap">
		{multiple && <MvvmSelect context={context}
                                 label={translation(PermitTranslations.new_residency__choose)}
                                 property="residencyId"
                                 items={choose}/>}

		{selectedResidency && <>
			{selectParts?.header?.(context.state)}
            <ResidencyComponent residency={selectedResidency}
                                installation={installation}
                                filesPrefix={filesPrefix}>
				{createEnabled && <button className={"card--edit-button"} onClick={editResidency}>
                    <Icon icon="document-edit" title={translation(PermitTranslations.edit)}/>
                </button>}
            </ResidencyComponent>
			{selectParts?.children}
            <FullCreateAttachmentsComponent context={context} attachments={attachments}/>

			{selectParts?.footer?.(context.state)}
        </>}

		{createVisible &&
            <CreateResidencyComponent context={context}
                                      configuration={configuration}
                                      zones={zones}
                                      cities={cities}
                                      provinces={provinces}
                                      autoFocus={autoFocus}
                                      hideBody={hideBody}
                                      header={createParts?.header}
                                      children={createParts?.children}
                                      footer={createParts?.footer}
                                      onAddressBlur={onAddressBlur}/>
		}
		</div>
		{footer?.(context.state)}
	</MvvmDataProvider>;
}
